import * as React from 'react';

import {ListItemText, MenuItem, useMediaQuery} from '@mui/material';
import Autocomplete, {autocompleteClasses} from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import {styled, useTheme} from '@mui/material/styles';
import {Button, Tooltip} from '@zoom/react-ui';
import {IconGlobe, IconChevronDownSmall, IconMagnifier, IconCheck, IconInformation} from '@zoom/sun-ui-icon';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';

import MobileDrawerMenu from 'Components/MobileDrawerMenu/MobileDrawerMenu';
import {ZmQuinaryBtn} from 'Components/ZmButtons';
import {ZmTextField} from 'Components/ZmInputs';

const StyledAutocompletePopper = styled('div')(({theme}) => ({
  overflowY: 'auto',
  height: 'calc(100% - 48px)',
  [`& .${autocompleteClasses.paper}`]: {
    height: '100%',
    overflowX: 'hidden',
    position: 'relative',
    fontSize: '0.875rem',
    boxShadow: 'unset',
    background: theme.palette.background.default,
  },
  [`& .${autocompleteClasses.listbox}`]: {
    padding: 0,
    maxHeight: '100%',
    [theme.breakpoints.up('sm')]: {
      maxHeight: 'calc(400px - 2px - 48px)',
    },
    [`& .${autocompleteClasses.option}`]: {
      'minHeight': 'auto',
      'alignItems': 'flex-start',
      'padding': '8px 0',
      '&[aria-selected="true"]': {
        backgroundColor: 'transparent',
      },
      [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]:
        {
          backgroundColor: theme.palette.action.hover,
        },
    },
  },
  [`&.${autocompleteClasses.popperDisablePortal}`]: {
    position: 'relative',
  },
}));

const StyledMenuItem = styled(MenuItem)(({theme}) => ({
  '&:hover': {
    backgroundColor: `${theme.palette.background.hover} !important`,
  },
}));
/**
 *
 * @param {Object} props
 * @return {ReactElement}
 */
function PopperComponent(props) {
  // eslint-disable-next-line no-unused-vars
  const {anchorEl, disablePortal, open, ...other} = props;
  return <StyledAutocompletePopper {...other} />;
}

PopperComponent.propTypes = {
  anchorEl: PropTypes.any,
  disablePortal: PropTypes.bool,
  open: PropTypes.bool.isRequired,
};

const StyledPopper = styled(Popper)(({theme}) => ({
  border: `1px solid ${theme.palette.common.swatch['30']}`,
  width: '24.0625rem',
  maxHeight: 400,
  display: 'flex',
  flexDirection: 'column',
  zIndex: theme.zIndex.modal,
  fontSize: '0.875rem',
  backgroundColor: theme.palette.common.white,
  borderRadius: '8px',
  boxShadow: '0px 12px 24px rgba(19, 22, 25, 0.1), 0px 6px 12px rgba(19, 22, 25, 0.1)',
  overflow: 'hidden',
}));

const MenuContainer = ({anchorEl, children, closeDrawer, handleClose, id, open}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {t} = useTranslation();
  return (isSmallScreen) ?
    <MobileDrawerMenu
      key="mobile-timezone-drawer"
      anchor={'bottom'}
      open={open}
      onCancel={closeDrawer}
      onDone={closeDrawer}
      title={t('timezonePicker.menuTitle')}
    >
      {children}
    </MobileDrawerMenu> :
    <StyledPopper id={id} open={open} anchorEl={anchorEl} placement="bottom-start">
      <ClickAwayListener onClickAway={handleClose}>
        {children}
      </ClickAwayListener>
    </StyledPopper>;
};

/**
 * @typedef TimezoneOption
 * @property {string} label
 * @property {string} value iana timezone string
 */

/**
 * @param {Object} props
 * @param {Function} props.onChange
 * @param {TimezoneOption} props.value
 * @param {Array<TimezoneOption>} props.options
 * @return {ReactElement}
 */
export default function TimezoneAutocomplete(props) {
  const {t} = useTranslation();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [inputValue, setInputValue] = React.useState('');
  const [isLockTooltipOpen, setIsLockTooltipOpen] = React.useState(false);

  React.useEffect(() => {
    setInputValue('');
  }, [anchorEl]);

  const handleClick = (event) => {
    // if time zone is locked, click will not open the auto complete
    if (props.locked) {
      setIsLockTooltipOpen(true);
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleSelect = (selectedOption) => {
    props.onChange && props.onChange(selectedOption);
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
  };

  const handleClose = () => {
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'timezone-selector' : undefined;

  const closeDrawer = (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
  };

  return (
    <React.Fragment>
      <Box sx={{minWidth: 221, ...props.sx}}>
        <ZmQuinaryBtn
          aria-describedby={id}
          onClick={handleClick}
          size="xsmall"
          aria-label={t('timezonePicker.ariaLabel')}
          sx={{fontWeight: 400, width: 'fit-content', color: theme.palette.text.primary, height: 'unset'}}
        >
          <IconGlobe size={'0.875rem'} sx={{marginRight: '6px'}} />
          {props.value?.label}
          <IconChevronDownSmall sx={{marginLeft: '4px'}}/>
        </ZmQuinaryBtn>
        {props.locked && <Tooltip open={isLockTooltipOpen} title={t('booking.timeZoneLockedByHost')} arrow
          onOpen={() => setIsLockTooltipOpen(true)}
          onClose={() => setIsLockTooltipOpen(false)}
        >
          <Button type="tertiary" variant="icon" size="small" aria-label="more">
            <IconInformation size={14} aria-hidden tabIndex={-1} />
          </Button>
        </Tooltip>}
      </Box>
      <MenuContainer
        id={id}
        anchorEl={anchorEl}
        open={open}
        handleClose={handleClose}
        closeDrawer={closeDrawer}
      >
        <div style={{flexGrow: 1, overflowY: 'auto'}}>
          <Autocomplete
            open
            onClose={(event, reason) => {
              if (reason === 'escape') {
                handleClose();
              }
            }}
            autoHighlight
            value={props.value}
            inputValue={inputValue}
            onChange={(event, newValue, reason) => {
              if (
                (event.type === 'keydown' &&
                  event.key === 'Backspace' &&
                  reason === 'removeOption') ||
                  reason === 'clear'
              ) {
                return;
              }
              handleSelect(newValue);
            }}
            PopperComponent={PopperComponent}
            renderTags={() => null}
            noOptionsText={t('timezonePicker.noMatches')}
            renderOption={(optionProps, option, {selected}) => (
              <StyledMenuItem
                data-testid={option.name}
                value={option.name}
                {...optionProps}
                onClick={() => handleSelect(option)}
                dense
                disableGutters
              >
                {selected ?
                    <>
                      <IconCheck size={16} sx={{paddingLeft: '8px', marginRight: '8px'}}/>
                      {option.label}
                    </> :
                    <ListItemText sx={{paddingLeft: '32px'}}>
                      {option.label}
                    </ListItemText>
                }
              </StyledMenuItem>
            )}
            options={props.options}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => (
              <ZmTextField
                size="small"
                aria-label={t('timezonePicker.searchInputLabel')}
                id="search-timezones"
                InputProps={{startAdornment: <IconMagnifier />}}
                fullWidth
                autoFocus
                sx={{padding: '8px', background: theme.palette.background.default}}
                ref={params.InputProps.ref}
                inputProps={params.inputProps}
                onChange={(e) => setInputValue(e.target.value)}
              />
            )}
          />
        </div>
      </MenuContainer>
    </React.Fragment>
  );
}
